/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace whitespace variable-name */

import {Injectable}            from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';

import {AuthenticationService} from './authentication.service';
import {
  Blob,
  BlobType
}                              from '@/models/blob';
import {User}                  from '@/models/user';
import {
  SimConfig,
  SimulationDispatchType,
  SimulationLogType
}                              from '@/models/sim/sim.config';
import {Logger}                from '@/util/logger';
import {BlobElement}           from '@/models/project/blob.element';
import {SimpleSimJob}          from '@/models/sim/simple.sim.job';
import {
  SimulationJob,
  SimulationJobType
}                              from '@/models/sim/simulation.job';
import {
  errorAsText,
  fromMySQLDate,
  isset
} from '@/util/util';
import {LineSimJobElement}     from '@/models/project/line.sim.job.element';
import {ProjectElementFactory} from '@/models/project.element.factory';
import {ProjectElementType}    from '@/models/project/project.element.type';
import {UUIDService}           from '@/services/uuid.service';
import {AlertService}          from '@/services/alert.service';

import {
  catchError,
  first,
  map,
  tap
}                              from 'rxjs/operators';
import {
  forkJoin,
  from,
  Observable, of
} from 'rxjs';

import * as $                  from 'jquery';
import 'gasparesganga-jquery-loading-overlay';
import {RemoteSimJob} from "@/models/sim/remote.sim.job";


@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class LineSimService
 *
 * @brief adaptor to work with a remote line simulator
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class LineSimService {

  /* - - - - - default parameters for launching simulations - - - - - - - - - */

  /** the kind of dispatch to use in the simulation */
  public       dispatch: SimulationDispatchType = SimulationDispatchType.WORKBLOX;

  /** if simulations run in permissive mode */
  public     permissive: boolean                = true;

  /** auto-launch the string line viewer after the simulation? */
  public     autoview: boolean                  = false;

  /** how long to wait for lockup detection (hours) */
  public    lockupHours: number                 = 720;

  /** max days to run the simulator for */
  public        maxDays: number                 = 30;

  /** passenger train reaction time (seconds) */
  public passengerReactionTime: number          = 0;

  /** freight train reaction time (seconds) */
  public freightReactionTime: number            = 0;

  /** maximum wall clock runtime for simulator (default is 1 hour or 3600 seconds) */
  public       maxTime: number                  = 3600;

  /** maximum memory usage by the simulator (default is 6GB of RAM) */
  public       maxMemory: number                = 1024*1024*1024*6;

  /** the verbosity level for simulation logging */
  public       logLevel: SimulationLogType      = SimulationLogType.ERROR;

  /** which simulator to use for actually doing the simulation. */
  public       endpoint: string                 = 'https://linesim.sn.ironroad.ca';

  public       engine: string                   = 'linesim';

  constructor(
    private http:   HttpClient,
    private auth:   AuthenticationService,
    private uuid:   UUIDService,
    private alerts: AlertService
  ) {

  }

  /** fetch any headers we need to send along with our request */
  headers(): HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', `Bearer ${user.token}`);
  }

  /** convert a string into a binary form but 8-bit ascii encoded.  Allow for unicode etc. */
  binaryKey(key: string): string {

    /* ref: https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings */

    const codeUnits = new Uint16Array(key.length);

    let byteArray: number[] = [];

    for (let i = 0; i < codeUnits.length; i++) {

      let byte: number = key.charCodeAt(i);

      /* low byte first */

      byteArray.push(byte & 0xFF);

      /* high byte second */

      byteArray.push((byte >> 8) & 0xFF);
    }

    return btoa(String.fromCharCode(...byteArray));
  }

  /**
   * fetch a download from the remote line simulator, and return the whole content (which might be big).
   * alternatively, you can provide a key etc.  and the blob with that key will be created and then
   * the blob meta data will be returned to you instead.  This allows you to fetch the content directly
   * if you want to, but also to delegate the download to the server so the transfer is "server to server"
   * and doesn't loop through the browser...which can save a lot of time, on a slow connection.
   *
   */
  public download(
    uri:         string,
    key:         string='',
    type:        BlobType=BlobType.File,
    name:        string='',
    description: string='',
    endpoint:    string='',
    engine:      string='',
    jobId:       number=0
  ) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    const params  = new HttpParams().set('uri', uri).
    set('key',          key.trim()).
    set('bkey',         this.binaryKey(key.trim())).
    set('type',         Blob.stringOfType(type)).
    set('name',         name).
    set('description',  description).
    set('endpoint',     endpoint).
    set('engine',       engine).
    set('jid',          jobId);

    /* pass back this hits */

    return this.http.get(`/backend/linesim/job/download`, {
      headers,
      params
    });
  }

  /** fetch the detailed progress info for all jobs given by the job ids */
  public progress(jobs: number[], endpoint: string='') {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    const params  = new HttpParams().set('jobs',    jobs.join(',')).set('endpoint', endpoint);

    /* pass back this hits */

    return this.http.get(`/backend/linesim/job/status`, {
      headers,
      params
    });
  }

  /** health check on one of the simulation engines */
  public ping(endpoint: string, engine: string): null|Observable<any> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      return null;
    }

    const params: HttpParams  = new HttpParams().
      set('endpoint', endpoint).
      set('engine',   engine);

    return this.http.get(`/backend/linesim/ping`, {
      headers,
      params
    });
  }

  /**
   *
   * list a single page of remote simulations.  This is provides support for paginated viewing
   * of all remote simulations.
   *
   * We return a list of jobs, similar to the recent() method, but here the job data is drawn
   * from the centralized simulations table, which is derived from the activity tracking, and
   * hence should be much more "real time" and detects jobs submitted directly to the remote
   * simulations as well as jobs submitted by the project manager.  This is the more complete
   * method that should supersede/replace the recent() method.
   *
   * Note:
   *
   * The POST body must be a JSON object, with attributes:
   *
   *   sort - must be one of:
   *     dateUp
   *     dateDown
   *     nameUp
   *     nameDown
   *
   *   pattern - the string or pattern to match against name or userid.
   *
   *   match - must be one of:
   *     substring
   *     exact
   *     simple
   *     regex
   *
   *   start - must be a range start, in format: yyyy-mm-dd hh:mm:ss
   *
   *   end - must be a range start, in format: yyyy-mm-dd hh:mm:ss
   *
   * All of the above can be omitted or given an empty/null value. The query element is only
   * used if provided and non-empty.
   *
   * For pattern matching modes, regex is fully regex, and simple mode is '*' wild cards
   * placed anywhere, matches 0 or more characters. substring is a partial string that can
   * occur anywhere to match.
   *
   * Query elements are applied by intersection; that is "boolean and"; if you supply both
   * a pattern and a date range, the result will be more narrow.
   *
   */
  public simulations(
    page:     number=1,
    pageSize: number=20,
    sort:     string='dateUp',
    pattern:  string='',
    match:    string='',
    start:    string='',
    end:      string='',
    simulators: string[]=[]) {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      return null;
    }

    let data: object = {
      sort,
      pattern,
      match,
      start,
      end,
      simulators
    };

    /* post the JSON request... */

    return this.http.post(`/backend/simulations/list/${page}/${pageSize}`, data, {
      headers
    });
  }

  /** fetch the detailed progress info for all jobs given by the job ids */
  public recent(endpoint: string, engine: string, filter: string, limit: number = 50) {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      return null;
    }

    const params: HttpParams  = new HttpParams().
      set('endpoint', endpoint).
      set('engine',   engine).
      set('filter',   filter).
      set('limit',    limit);

    /* pass back this hits */

    return this.http.get(`/backend/linesim/job/recent`, {
      headers,
      params
    });
  }

  /** try to map URL for a remote simulation engine to its engine tag */
  public engineOfEndpoint(url: string): string {

    let urlToTag: Map<string, string> = new Map<string, string>([
      ['cloudsim-sn-storage',          'cloudsim'],
      ['cloudsim-local-sn-storage',    'cloudsim-local'],
      ['cloudsim-local-sn-storage',    'cloudsim-unique'],
      ['linesim-sn-storage',           'linesim'],
      ['linesim-local-sn-storage',     'linesim-local'],
      ['linesim-local-sn-storage',     'linesim-unique'],
      ['linesimbeta-sn-storage',       'linesimbeta'],
      ['linesimbeta-local-sn-storage', 'linesimbeta-local'],
      ['linesimbeta-local-sn-storage', 'linesimbeta-unique'],

    ]);

    for (let [k,v] of urlToTag.entries()) {

      let re: RegExp = new RegExp(`[:/]${k}`);

      if (re.test(url)) {

        return v;
      }
    }

    /* no match */

    return '';
  }

  /** fetch the current default configuration */
  public getConfiguraiton(): SimConfig {

    return new SimConfig({
       priority:              1.0,
       autoview:              false,
       permissive:            this.permissive,
       logLevel:              this.logLevel,
       lockupHours:           this.lockupHours,
       dispatch:              this.dispatch,
       maxDays:               this.maxDays,
       maxTime:               this.maxTime,
       maxMemory:             this.maxMemory,
       passengerReactionTime: this.passengerReactionTime,
       freightReactionTime:   this.freightReactionTime,
       endpoint:              this.endpoint,
       engine:                this.engine
    });
  }

  /** set the default parameters for launching a simulation */
  public configure(config : SimConfig): void {

    this.dispatch              = config.dispatch;
    this.permissive            = config.permissive;
    this.autoview              = config.autoview;
    this.lockupHours           = config.lockupHours;
    this.maxDays               = config.maxDays;
    this.logLevel              = config.logLevel;
    this.passengerReactionTime = config.passengerReactionTime,
    this.freightReactionTime   = config.freightReactionTime,
    this.endpoint              = config.endpoint;
    this.engine                = config.engine;

    Logger.debug('[linesim] default configuration: ', config);
  }

  /**
   * given some number of File that were uploaded to the browser, and a configuration
   * to use for simulating them...queue them up for simulation at the simulation engine
   * implied by the configuration.  Once they are all queued, return back SimulationJob
   * object for each so we have a handle to use for knowing the progress etc.
   *
   *
   * @param scenarios the list of uploaded scenario files
   * @param config the common configuration to use for simulating them
   *
   */
  public launchScenarios(scenarios: File[], config: SimConfig): Promise<SimulationJob[]> {

    return new Promise((resolve, reject): void => {

      Logger.debug('[linesim] launchScenarios - uploads: ', scenarios);
      Logger.debug('[linesim] launchScenarios - config: ',  config);

      const headers: HttpHeaders = this.headers();

      if (!headers) {

        return reject('could not build headers.');
      }

      // @ts-ignore
      $.LoadingOverlay('show', {

        text: `Preparing ${scenarios.length} scenarios for simulation...`,
        progress: 0,
        background: 'rgba(255, 255, 255, 0.85)',
        textColor: 'black',
        imageColor: 'black'
      });

      /* create the launch requests... */

      Logger.debug('[linesim] launchScenarios - creating requests...');

      const   observables:  any[] = [];

      for (const file of scenarios) {

        /* set the required args ... */

        const form: FormData = new FormData();

        /* merge / default the optional parameters */

        const passthrough: number           = 1;
        let priority: number                = 1.0;
        let permissive: number              = (config.permissive === true) ? 1 : 0;
        let loglevel: string                = SimConfig.logLevelString(config.logLevel);
        let lockup_hours: number            = config.lockupHours;
        let dispatch: string                = SimConfig.dispatchString(config.dispatch);
        let max_days: number                = config.maxDays;
        let max_time: number                = config.maxTime;
        let max_memory: number              = config.maxMemory;
        let passenger_reaction_time: number = config.passengerReactionTime;
        let freight_reaction_time: number   = config.freightReactionTime;

        /* add the option fields to the form ... */

        form.set('permissive',              permissive.toString());
        form.set('passthrough',             passthrough.toString());
        form.set('priority',                priority.toString());
        form.set('loglevel',                loglevel);
        form.set('lockup_hours',            lockup_hours.toString());
        form.set('dispatch',                dispatch);
        form.set('max_days',                max_days.toString());
        form.set('max_time',                max_time.toString());
        form.set('max_memory',              max_memory.toString());
        form.set('passenger_reaction_time', passenger_reaction_time.toString());
        form.set('freight_reaction_time',   freight_reaction_time.toString());

        /* also ensure we are selecting the right remote simulator to use */

        form.set('endpoint',     config.endpoint);
        form.set('engine',       config.engine);

        form.set('scenario', file);

        /* config for this particular scenario */

        Logger.debug('[linesim] launchScenarios - . enqueuing:', form);

        const observable: Observable<any> = this.http.post(`/backend/linesim/enqueue/scenario`, form, {
          headers
        });

        observables.push(observable.pipe(catchError((error: any) => {

          /*
           * forkJoin() will
           * fail if even one of them is an error, so we have to hide errors from
           * Angular and deal with them on our own in the success clause at the end.
           *
           */

          Logger.error('[linesim] launchScenarios - error: ', error);

          return of({
            isError: true,
            error,
            text: errorAsText(error)
          });

        })).pipe(first()));

      } /* end of creating the observables */

      Logger.debug('[linesim] launchScenarios - launching ...');

      // @ts-ignore
      $.LoadingOverlay('text', `Launching...`);

      /* launch them in parallel... */

      const N: number = scenarios.length;
      let   n: number = 0;

      forkJoin(
        observables.map(o => o.pipe(tap(() => {

          n++;

          const progress: string = ((n/N) * 100).toFixed(2);

          // @ts-ignore
          $.LoadingOverlay('text', `[${progress}%] launching...`);

        })))).subscribe({

          next: (results: any[]): void => {

          Logger.debug('[linesim] launchScenarios - joined: ', results);

          // @ts-ignore
          $.LoadingOverlay('hide');

          /* separate out the errors from the valid results, because it might be partially completed */

          const   errors: any[]     = results.filter(x => 'isError' in x);
          const    valid: any[]     = results.filter(x => !('isError' in x));

          /* "up-convert" the JSON response for each job to an actual SimulationJob object... */

          let jobs: SimulationJob[] = [];

          for (let data of valid) {

            /* build the RemoteSimJob */

            let details: RemoteSimJob = new RemoteSimJob({...data.job});

            /* build the config */

            let jobConfig: SimConfig = new SimConfig({
              dispatch:              SimConfig.dispatchType(data.job.options.dispatch),
              maxDays:               data.job.options.max_days,
              maxTime:               data.job.options.max_time,
              maxMemory:             data.job.options.max_memory,
              lockupHours:           data.job.options.lockup_hours,
              logLevel:              SimConfig.logLevelType(data.job.options.loglevel),
              permissive:            data.job.options.permissive,
              priority:              1.0,
              autoview:              false,
              userlog:               data.job.options.userlog,
              traceStart:            data.job.options.trace_start,
              traceEnd:              data.job.options.trace_end,
              passengerReactionTime: data.job.options.passenger_reaction_time,
              freightReactionTime:   data.job.options.freight_reaction_time,
              endpoint:              config.endpoint,
              engine:                config.engine
            });

            /* build the job */

            const job: SimpleSimJob = new SimpleSimJob({
              type:     SimulationJobType.SIMPLE_SIM,
              jobId:    data.job.id,
              status:   SimulationJob.statusType(data.job.status),
              created:  fromMySQLDate(data.job.created_at),
              dequeue:  fromMySQLDate(data.job.dequeue_at),
              updated:  fromMySQLDate(data.job.updated_at),
              config:   jobConfig,
              details
            });

            /* save it! */

            jobs.push(job);
          }

          /* process the ones that failed */

          if (errors.length > 0) {

            Logger.error('[linesim] launchScenarios - batch join had some errors: ', errors);
          }

          /* let the initiator know we have results! */

          resolve(jobs);
        },
        error: (err: any): void => {

          Logger.error('[linesim] launchScenarios - could not join: ', err);

          // @ts-ignore
          $.LoadingOverlay('hide');

          this.alerts.error(`Could not completely launch batch: ` + errorAsText(err));

          /* let the initiator know we failed */

          reject(err);
        }

      });

    }); /* end of promise creation */
  }

  /** convenience to launch batch of simulations */
  public multiSim(scenarios: BlobElement[], config: SimConfig): Promise<LineSimJobElement[]> {

    return new Promise((resolve, reject) => {

      Logger.debug('[linesim] batch simulation: ', scenarios);

      /*
       * just re-use singleSim() (gives a Promise); convert the promise to an Observable,
       * and then we can do the usual forJoin mechanics.
       *
       */

      /* build out the observables... */

      // @ts-ignore
      $.LoadingOverlay('show', {

        text: `Preparing ${scenarios.length} scenarios for simulation...`,
        progress: 0,
        background: 'rgba(255, 255, 255, 0.85)',
        textColor: 'black',
        imageColor: 'black'
      });

      const   observables:  any[] = [];

      for (const blob of scenarios) {

        const futureJob: Promise<LineSimJobElement> = this.singleSim(blob, config);
        const observable: Observable<LineSimJobElement> = from(futureJob);

        observables.push(observable.pipe(catchError((error: any) => {

            /*
             * as we process the launch requests, convert errors to "fake" results so we
             * can allow partial completion instead of all or nothing.  forkJoin() will
             * fail if even one of them is an error, so we have to hide errors from
             * Angular and deal with them on our own in the success clause at the end.
             *
             */

            Logger.error('[linesim] batching error: ', error);

            /*
             * pass back a fake result, give as much context as we can, we aren't trying
             * to do all the error capturing here; the LineSimJobElements actually have
             * all that detail already.  We just want the forkJoin() to not be brittle
             * and completely break on any error...so we get as many results finished as
             * we can.
             *
             */

            return of({
                isError: true,
                error,
                text: errorAsText(error)
              });
          }

        )).pipe(first()));

      } /* end of creating the observables */

      // @ts-ignore
      $.LoadingOverlay('text', `Launching...`);

      Logger.debug('[linesim] forking...');

      const N: number = scenarios.length;
      let   n: number = 0;

      forkJoin(
        observables.map(o => o.pipe(tap(() => {

          n++;

          const progress: string = ((n/N) * 100).toFixed(2);

          // @ts-ignore
          $.LoadingOverlay('text', `[${progress}%] launching...`);

        })))).subscribe(

        (results: any[]) => {

          Logger.debug('[linesim] joined: ', results);

          // @ts-ignore
          $.LoadingOverlay('hide');

          /* separate out the errors from the valid results, because it might be partially completed */

          const   errors: any[]               = results.filter(x => 'isError' in x);
          const    valid: LineSimJobElement[] = results.filter(x => x instanceof LineSimJobElement);

          /* process the ones that failed */

          if (errors.length > 0) {

            Logger.error('[linesim] batch join had some errors: ', errors);
          }

          /* let the initiator know we have results! */

          resolve(valid);
        },
        (error: any) => {

          Logger.debug('[linesim] could not join: ', error);

          // @ts-ignore
          $.LoadingOverlay('hide');

          this.alerts.error(`Could not completely launch batch: ` + errorAsText(error));

          /* let the initiator know we failed */

          reject(error);
        });

    });
  }

  /** convenience to launch a simulation and (via Promise) let you know when it resolved (or not) */
  public singleSim(scenarioBlob: BlobElement, config: SimConfig): Promise<LineSimJobElement> {

    return new Promise((resolve, reject) => {

      Logger.debug('[linesim] launching blob sim of: ', scenarioBlob);

      /* generate a uuid for everything related to this job */

      this.uuid.generate()
        .pipe(first())
        .subscribe(
          (result: any) => {

            /* we have a UUID, create the configuration */

            const uuid   = result.guid;

            Logger.debug(`[SimButton] configuring for new sim job`, uuid, config);

            /* try to launch the job ... */

            this.launch(scenarioBlob._blobKey, scenarioBlob._blobInstance, config)
              .pipe(first())
              .subscribe(
                (queued: any): void => {

                  /* ok, we launched! (create our local model of the job) */

                  const job: SimpleSimJob = new SimpleSimJob({
                    type:     SimulationJobType.SIMPLE_SIM,
                    jobId:    queued.job_id,
                    jobUuid:  uuid,
                    status:   SimulationJob.statusType(queued.status),
                    created:  fromMySQLDate(queued.created),
                    dequeue:  fromMySQLDate(queued.dequeue),
                    updated:  fromMySQLDate(queued.updated),
                    key:      scenarioBlob._blobKey,
                    instance: scenarioBlob._blobInstance,
                    config
                  });

                  /* add a new leaf to the blob for the result this will generate */

                  const el: LineSimJobElement = ProjectElementFactory.fromObject(
                    ProjectElementType.LineSimResult,
                    {
                      project: scenarioBlob._project,
                      id: '',
                      job,
                      parent: scenarioBlob,
                      publish: true
                    }) as LineSimJobElement;

                  resolve(el);
                },
                (error) => {

                  /* let the launcher know we couldn't even launch... */

                  reject(error);

                });
          },
          (error) => {

            /* let the launcher know we couldn't even launch... */

            reject(error);
          });

    });
  }

  /** delete the given remote simulation job */
  public delete(jobId: number[], config: SimConfig = null) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* we're expecting to get gs:// style URLs here */

    const params: HttpParams  = new HttpParams().
    set('jid',      jobId.join(',')).
    set('endpoint', config.endpoint);

    return this.http.delete(`/backend/linesim/delete`, {
      headers,
      params,
      responseType: 'json'
    });
  }

  /** launch a remote simulation in the line simulator, intended only for quick one off simulations */
  public launch(key: string, instance: string = 'latest', config: SimConfig = null) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* set the required args ... */

    const form = new FormData();

    form.set('key',  key);
    form.set('bkey', this.binaryKey(key));
    form.set('instance', instance);

    /* merge / default the optional parameters */

    const passthrough: number           = 1;
    let priority: number                = 1.0;
    let permissive: number              = (this.permissive === true) ? 1 : 0;
    let loglevel: string                = SimConfig.logLevelString(this.logLevel);
    let lockup_hours: number            = this.lockupHours;
    let dispatch: string                = SimConfig.dispatchString(this.dispatch);
    let max_days: number                = this.maxDays;
    let max_time: number                = this.maxTime;
    let max_memory: number              = this.maxMemory;
    let auto_view: number               = (this.autoview === true) ? 1 : 0;
    let userlog: string                 = '';
    let trace_start: string             = '';
    let trace_end: string               = '';
    let passenger_reaction_time: number = this.passengerReactionTime;
    let freight_reaction_time: number   = this.freightReactionTime;

    /* do any option overrides ... */

    if (config instanceof SimConfig) {

      if (config?.maxDays !== null) {

        max_days = config.maxDays;
      }

      if (config?.maxTime !== null) {

        max_time = config.maxTime;
      }

      if (config?.maxMemory !== null) {

        max_memory = config.maxMemory;
      }

      if (config?.dispatch !== null) {

        dispatch = SimConfig.dispatchString(config.dispatch);
      }

      if (config?.logLevel !== null) {

        loglevel = SimConfig.logLevelString(config.logLevel);
      }

      if (config?.lockupHours !== null) {

        lockup_hours = config.lockupHours;
      }

      if (config?.priority !== null) {

        priority = config.priority;
      }

      if (config?.permissive !== null) {

        permissive = (config.permissive === true) ? 1 : 0;
      }

      if (config?.autoview !== null) {

        auto_view = (config.autoview === true) ? 1 : 0;
      }

      if ((config?.userlog !== null) && (config.userlog != '')) {

        userlog = config.userlog;
      }

      if ((config?.traceStart !== null) && (config.traceStart != '')) {

        trace_start = config.traceStart;
      }

      if ((config?.traceEnd !== null) && (config.traceEnd != '')) {

        trace_end = config.traceEnd;
      }
    }

    /* add the option fields to the form ... */

    form.set('permissive',              permissive.toString());
    form.set('passthrough',             passthrough.toString());
    form.set('priority',                priority.toString());
    form.set('loglevel',                loglevel);
    form.set('lockup_hours',            lockup_hours.toString());
    form.set('dispatch',                dispatch);
    form.set('max_days',                max_days.toString());
    form.set('max_time',                max_time.toString());
    form.set('max_memory',              max_memory.toString());
    form.set('passenger_reaction_time', passenger_reaction_time.toString());
    form.set('freight_reaction_time',   freight_reaction_time.toString());

    if (userlog != '') {

      /* if we are in debug mode, then userlog will have been set, along with the tracing window */

      form.set('userlog',               userlog);
      form.set('trace_start',           trace_start);
      form.set('trace_end',             trace_end);
    }

    /* also ensure we are selecting the right remote simulator to use */

    form.set('endpoint',                config.endpoint);

    Logger.debug('[linesim] enqueuing:', form);

    /* pass back job id data */

    return this.http.post(`/backend/linesim/enqueue`, form, {
      headers
    });
  }

  /** fetch the contents of a simulation artifact, format can be 'arraybuffer' | 'blob' | 'json' | 'text' */
  artifactContents(path: string, format: string='text'): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[linesim] contents - no user logged in!`);

      return null;
    }

    /* we're expecting to get gs:// style URLs here */

    const params: HttpParams  = new HttpParams().
      set('path',      path).
      set('bpath',     this.binaryKey(path));

    /*
     * pass back content of a blob, not parsed, just the content.  We may later add
     * a binary method for contents() which casts the response as an array buffer or
     * a blob.  For now just using text, since not all blobs will be JSON format.
     *
     */

    switch (format) {

      case 'arraybuffer':
        return this.http.get(`/backend/linesim/artifact/download`, {
          headers,
          params,
          responseType: 'arraybuffer'
        });

      case 'blob':
        return this.http.get(`/backend/linesim/artifact/download`, {
          headers,
          params,
          responseType: 'blob'
        });

      case 'json':
        return this.http.get(`/backend/linesim/artifact/download`, {
          headers,
          params,
          responseType: 'json'
        });

      case 'text':
        return this.http.get(`/backend/linesim/artifact/download`, {
          headers,
          params,
          responseType: 'text'
        });

      default: return null;

    }

  }

}
